import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, CalendarClock, Plus, Pencil, Trash2 } from "lucide-react";
import { billingRuleService, BillingRule } from "@/services/billingRule";
import { MessageTemplate } from "@/services/messageTemplate";
import { CreateBillingRuleModal } from "@/components/billing-rules/CreateBillingRuleModal";
import { UpdateBillingRuleModal } from "@/components/billing-rules/UpdateBillingRuleModal";

interface BillingRulesTabProps {
  templates: MessageTemplate[];
}

export function BillingRulesTab({ templates }: BillingRulesTabProps) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editRule, setEditRule] = useState<BillingRule | null>(null);

  const { data: rulesData, isLoading } = useQuery({
    queryKey: ["billing-rules"],
    queryFn: () => billingRuleService.getAll({ limit: 50 }),
  });

  const rules = rulesData?.data ?? [];
  const templateNameById = new Map(templates.map((t) => [t.id, t.name]));

  const toggleMutation = useMutation({
    mutationFn: (id: number) => billingRuleService.toggle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["billing-rules"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => billingRuleService.remove(id),
    onSuccess: () => {
      toast.success("Regla de cobro eliminada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["billing-rules"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div>
      <div className="flex items-center justify-end mb-4">
        <Button size="sm" onClick={() => setCreateOpen(true)} disabled={templates.length === 0}>
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Nueva regla
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : rules.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2">
          <CalendarClock className="w-8 h-8 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">Sin reglas de envío</p>
          {templates.length === 0 && (
            <p className="text-xs text-muted-foreground/70">
              Crea primero una plantilla de mensaje.
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Plantilla</TableHead>
                <TableHead>Evento</TableHead>
                <TableHead>Días</TableHead>
                <TableHead>Hora</TableHead>
                <TableHead>Activa</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.id} className="hover:bg-muted/30">
                  <TableCell className="font-medium text-foreground">{rule.name}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {templateNameById.get(rule.templateId) ?? `#${rule.templateId}`}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-[10px]">
                      {rule.triggerEvent}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs tabular-nums">
                    {rule.daysOffset > 0
                      ? `${rule.daysOffset}d antes`
                      : rule.daysOffset === 0
                        ? "mismo día"
                        : `${Math.abs(rule.daysOffset)}d después`}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs font-mono">
                    {rule.sendTime}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={rule.isActive}
                      disabled={toggleMutation.isPending}
                      onCheckedChange={() => toggleMutation.mutate(rule.id)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setEditRule(rule)}
                    >
                      <Pencil className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>¿Eliminar regla?</AlertDialogTitle>
                          <AlertDialogDescription>
                            La regla <strong>{rule.name}</strong> será eliminada permanentemente. Esta acción no se puede deshacer.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteMutation.mutate(rule.id)}
                            disabled={deleteMutation.isPending}
                            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                          >
                            Eliminar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateBillingRuleModal open={createOpen} onOpenChange={setCreateOpen} templates={templates} />
      {editRule && (
        <UpdateBillingRuleModal
          open={!!editRule}
          onOpenChange={(open) => !open && setEditRule(null)}
          rule={editRule}
          templates={templates}
        />
      )}
    </div>
  );
}
